import {
  type CreateMutationOptions,
  type CreateMutationResult,
  type DefaultError,
  type MutateOptions,
  createMutation as createBaseMutation,
} from '@tanstack/svelte-query';

type Callback = (...args: unknown[]) => unknown;

/**
 * `createMutation` whose per-call `onSuccess`/`onError`/`onSettled` always run once the request
 * finishes. TanStack drops them when the component that called `mutate` is gone by then, and a
 * Svelte component goes as soon as the refetched data no longer has it (a removed member's row, an
 * appointment row keyed by its version); React re-rendered later, so its callbacks still ran.
 */
export function createMutation<TData = unknown, TError = DefaultError, TVariables = void, TContext = unknown>(
  options: () => CreateMutationOptions<TData, TError, TVariables, TContext>,
): CreateMutationResult<TData, TError, TVariables, TContext> {
  const base = createBaseMutation(options);
  const mutate = (variables: TVariables, callbacks?: MutateOptions<TData, TError, TVariables, TContext>) => {
    const call = (fn: unknown, ...args: unknown[]) => void (fn as Callback | undefined)?.(...args);
    base.mutateAsync(variables).then(
      (data) => {
        call(callbacks?.onSuccess, data, variables, undefined, undefined);
        call(callbacks?.onSettled, data, null, variables, undefined, undefined);
      },
      (error: unknown) => {
        call(callbacks?.onError, error, variables, undefined, undefined);
        call(callbacks?.onSettled, undefined, error, variables, undefined, undefined);
      },
    );
  };
  return new Proxy(base, {
    get: (target, prop) => (prop === 'mutate' ? mutate : (Reflect.get(target, prop) as unknown)),
  });
}
